import { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import MfaService from '../../../shared/services/mfa/mfa.service';
import { IOrganisationMfa } from '../../../shared/services/mfa/mfa.model';
import { MfaEnforcementSection } from '../../presentation-components/settings-components/mfa-enforcement-section';

// Admin-only page under "Admin settings": turn organisation-wide two-factor
// authentication on or off, and see/adjust each user's method.
export const SettingsOrgTwoFactorPage = () => {
  const mfaService = useMemo(() => new MfaService(), []);
  const userNotifications = useContext(UserNotificationsContext);

  const [data, setData] = useState<IOrganisationMfa | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = () => mfaService.getOrganisationMfa()
    .then(setData)
    .catch(() => { /* the http interceptor handles auth errors */ })
    .finally(() => setLoading(false));

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorToast = (err?: any) => userNotifications.openSnackbar({
    open: true,
    message: err?.response?.data?.message || 'There was an error, please try again',
    backgroundColor: 'text.primary',
  });

  const onToggleEnforcement = async (next: boolean) => {
    setBusy(true);
    try {
      const res = await mfaService.setOrganisationEnforcement(next);
      await refresh();
      userNotifications.openSnackbar({
        open: true,
        message: next
          ? `Two-factor authentication is now required${res?.movedToEmail ? ` (${res.movedToEmail} moved to email)` : ''}`
          : 'Two-factor authentication is no longer required',
      });
    } catch (err) {
      errorToast(err);
    } finally {
      setBusy(false);
    }
  };

  const onSetToEmail = async (userId: string) => {
    setBusy(true);
    try {
      await mfaService.setUserToEmail(userId);
      await refresh();
      userNotifications.openSnackbar({ open: true, message: 'User set to email codes' });
    } catch (err) {
      errorToast(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageOuterContainer>
      <PageHeading title="Two-factor authentication" hasTabs={false} />
      <Container maxWidth={false} sx={{ px: 0 }}>
        {data?.ssoEnforced && (
          <Alert severity="warning" sx={{ mb: 3, maxWidth: 640 }}>
            These settings are not currently in use. Single sign-on is required for your organisation, so everyone signs in through your identity provider and two-factor authentication is handled there.
          </Alert>
        )}
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640, mb: 3 }}>
          Require a second step for everyone who signs in with a password, and manage each person&apos;s method.
        </Typography>
        {loading ? (
          <CircularProgress size={22} />
        ) : data ? (
          <Stack spacing={2}>
            <MfaEnforcementSection
              enforceMfa={data.enforceMfa}
              users={data.users}
              busy={busy}
              onToggleEnforcement={onToggleEnforcement}
              onSetToEmail={onSetToEmail}
            />
          </Stack>
        ) : null}
      </Container>
    </PageOuterContainer>
  );
};
